import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './FilterPanel.css';
import FacetFilter from './FacetFilter';
import DateRangeFilter from './DateRangeFilter';
import AppliedFilters from './AppliedFilters';
import { loadFilterConfiguration, refreshFacetData } from '../store/filterThunks';
import {
  selectAvailableFilters,
  selectFiltersLoading,
  selectFiltersError,
  selectProjectId,
  selectPendingFilters,
  selectPendingDateFilters,
  selectAppliedFilters,
  selectDateFilters,
  selectFacetDataLoaded,
  initializePendingFilters,
  applyPendingFilters,
  resetPendingToApplied
} from '../store/filterSlice';

function FilterPanel({ isOnResultPage = false }) {
  const dispatch = useDispatch();
  const availableFilters = useSelector(selectAvailableFilters);
  const loading = useSelector(selectFiltersLoading);
  const error = useSelector(selectFiltersError);
  const projectId = useSelector(selectProjectId);
  const facetDataLoaded = useSelector(selectFacetDataLoaded);

  // Filter state for buttons (only when on result page)
  const pendingFilters = useSelector(selectPendingFilters);
  const pendingDateFilters = useSelector(selectPendingDateFilters);
  const appliedFilters = useSelector(selectAppliedFilters);
  const appliedDateFilters = useSelector(selectDateFilters);

  useEffect(() => {
    dispatch(loadFilterConfiguration({ projectId, forceRefresh: false }));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (isOnResultPage) {
      dispatch(initializePendingFilters());
    }
  }, [dispatch, isOnResultPage]);

  // Check if there are pending changes (copied from FilterApply)
  const hasPendingChanges = () => {
    if (!isOnResultPage) return false;

    // Check if there are any differences between applied and pending filters
    const pendingKeys = Object.keys(pendingFilters);
    const appliedKeys = Object.keys(appliedFilters);

    // Check facet filters
    if (pendingKeys.length !== appliedKeys.length) return true;

    for (const key of pendingKeys) {
      const pendingValues = pendingFilters[key] || [];
      const appliedValues = appliedFilters[key] || [];

      if (pendingValues.length !== appliedValues.length) return true;
      if (!pendingValues.every(val => appliedValues.includes(val))) return true;
    }

    // Check date filters
    const pendingDateKeys = Object.keys(pendingDateFilters);
    const appliedDateKeys = Object.keys(appliedDateFilters);

    if (pendingDateKeys.length !== appliedDateKeys.length) return true;

    for (const key of pendingDateKeys) {
      const pending = pendingDateFilters[key];
      const applied = appliedDateFilters[key];

      if (!applied || pending.startDate !== applied.startDate || pending.endDate !== applied.endDate) {
        return true;
      }
    }

    return false;
  };

  const handleApply = () => {
    dispatch(applyPendingFilters());
  };

  const handleClear = () => {
    dispatch(resetPendingToApplied());
  };

  const handleReload = () => {
    // Force refresh both configuration and facets
    dispatch(loadFilterConfiguration({ projectId, forceRefresh: true }));
  };

  if (loading) {
    return (
      <div className="filter-panel">
        <div className="filter-loading">
          <div className="filter-loading-spinner"></div>
          <p>Loading filters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="filter-panel">
        <div className="filter-error">
          <p>Error loading filters: {error}</p>
        </div>
      </div>
    );
  }

  if (availableFilters.length === 0) {
    return (
      <div className="filter-panel">
        <div className="filter-header">
          <h3>Filters</h3>
        </div>
        <div className="filter-no-data">
          <p>No filters found</p>
        </div>
      </div>
    );
  }

  const renderFilter = (filter) => {
    // Only render filters where filters: true
    if (!filter.filters) {
      return null;
    }

    switch (filter.type) {
      case 'tags':
      case 'string':
      case 'string_lower':
        return <FacetFilter key={filter.value} filter={filter} isOnResultPage={isOnResultPage} />;
      case 'date':
      case 'pdate':
        return <DateRangeFilter key={filter.value} filter={filter} isOnResultPage={isOnResultPage} />;
      default:
        // For any other field type, use FacetFilter as default
        return <FacetFilter key={filter.value} filter={filter} isOnResultPage={isOnResultPage} />;
    }
  };

  return (
    <div className="filter-panel">
      <div className="filter-header">
        <h3>Filters</h3>
        <div className="filter-header-buttons">
          <button
            className="filter-header-reload-btn"
            onClick={handleReload}
            title="Reload filters"
            disabled={loading}
          >
            ↻
          </button>
          {isOnResultPage && hasPendingChanges() && (
            <>
              <button
                className="filter-header-clear-btn"
                onClick={handleClear}
                title="Reset to current applied filters"
              >
                Clear
              </button>
              <button
                className="filter-header-apply-btn"
                onClick={handleApply}
                title="Apply selected filters"
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      <AppliedFilters />

      <div className="filter-sections">
        {availableFilters.map(renderFilter)}
      </div>
    </div>
  );
}

export default FilterPanel;